import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, FilterSegment } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

// Отчёт «Скидки»: все скидки чеков через единый справочник сервисов —
// правила кабинета, ручные скидки кассира, акции агрегаторов, промокоды

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "14px",
	boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
}

const mutedStyle = { color: "var(--dark-text-muted, #8791a1)", fontSize: "12px" }

const cellStyle = { borderBottom: "1px solid var(--dark-border, #eef2f6)" }

// Цвет источника скидки — один и тот же в селекте, таблице и карточках
const SOURCE_COLORS = {
	rule: { color: "#2563eb", bg: "var(--dark-tint-primary, #e8f0fe)" },
	cashier: { color: "#e0762c", bg: "var(--dark-tint-warning, #fff4df)" },
	aggregator: { color: "#16a34a", bg: "var(--dark-tint-success, #e7f8ef)" },
	partner_api: { color: "#0891b2", bg: "var(--dark-tint-info, #e6f6fa)" },
	promo_code: { color: "#7c3aed", bg: "var(--dark-tint-info, #f1eafe)" },
}

const SOURCE_IDS = ["rule", "cashier", "aggregator", "partner_api", "promo_code"]

function KpiCard({ title, value, sublabel, icon, color = "#2563eb" }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					<div className="d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", borderRadius: "11px", background: `${color}18`, color }}>
						<i className={`uil ${icon}`}></i>
					</div>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>{value}</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function SourceBadge({ source, t }) {
	if (!source) return null
	const meta = SOURCE_COLORS[source] || SOURCE_COLORS.rule
	return (
		<span style={{ background: meta.bg, color: meta.color, borderRadius: "12px", padding: "2px 8px", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap" }}>
			{t(`discount_source_${source}`)}
		</span>
	)
}

function DiscountsTable({ rows = [], totals = {}, groupBy, t }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }
	const totalAmount = Number(totals.amount || 0)
	const groupLabel = {
		service: t("discount_service"),
		source: t("discount_source"),
		cashier: t("cashier"),
		pos: t("branch"),
		day: t("date"),
		product: t("product"),
	}[groupBy]

	function rowName(row) {
		if (groupBy === "source") return t(`discount_source_${row.id}`)
		return row.name || "—"
	}

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				{groupBy === "cashier" && <div className="mb-2" style={mutedStyle}>{t("discounts_cashier_hint")}</div>}
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>{groupLabel}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("cheques")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("discount_items")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("discount_lines")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("discount_reduced")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("discount_gift_nominal")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("discounts_total")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("share")}</th>
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && <tr><td colSpan="8" className="text-muted text-center py-4">{t("list_empty")}</td></tr>}
							{rows.map(row => {
								const amount = Number(row.amount || 0)
								return (
									<tr key={row.id ?? "none"}>
										<td className="py-3" style={cellStyle}>
											<div className="d-flex align-items-center gap-2 flex-wrap">
												<span style={{ fontWeight: 700 }}>{rowName(row)}</span>
												{groupBy === "service" && <SourceBadge source={row.source} t={t} />}
											</div>
										</td>
										<td className="py-3 text-end" style={cellStyle}>{formatMoney(row.cheques_count, 0)}</td>
										<td className="py-3 text-end" style={cellStyle}>{Number(row.items_count) > 0 ? formatMoney(row.items_count, 0) : "—"}</td>
										<td className="py-3 text-end text-muted" style={cellStyle}>{formatMoney(row.lines_count, 0)}</td>
										<td className="py-3 text-end" style={cellStyle}>{formatMoney(row.reduced_amount || 0)}</td>
										<td className="py-3 text-end text-muted" style={cellStyle}>{Number(row.nominal_amount) > 0 ? formatMoney(row.nominal_amount) : "—"}</td>
										<td className="py-3 text-end" style={{ ...cellStyle, fontWeight: 800 }}>{formatMoney(amount)}</td>
										<td className="py-3 text-end text-muted" style={cellStyle}>{totalAmount > 0 ? formatMoney(amount * 100 / totalAmount, 1) : 0}%</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)
	const [services, setServices] = useState([])

	const filters = useAnalyticsFilters({
		...buildPeriod("30days"),
		pos_id: reduxAccount.pos_id ?? -1,
		order_type_ids: [],
		source_ids: [],
		sources: [],
		discount_service_ids: [],
		group_by: "service",
	}, searchWithFilter)
	const { draft, patch } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/discounts", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	async function getServices() {
		const response = await GET("/analytics/discount-services")
		if (httpOk(response)) setServices(response.data)
	}

	useEffect(() => {
		getServices()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const sourceOptions = useMemo(() => SOURCE_IDS.map(id => ({ id, name: t(`discount_source_${id}`) })), [t])

	// В селекте сервиса — только выбранные источники, чтобы список не разрастался
	const serviceOptions = useMemo(() => {
		const sources = draft.sources || []
		return services
			.filter(service => sources.length === 0 || sources.includes(service.source))
			.map(service => ({ ...service, label: `${service.name} · ${t(`discount_source_${service.source}`)}` }))
	}, [services, draft.sources, t])

	const groupOptions = [
		{ id: "service", label: t("discounts_group_service") },
		{ id: "source", label: t("discounts_group_source") },
		{ id: "cashier", label: t("discounts_group_cashier") },
		{ id: "pos", label: t("discounts_group_pos") },
		{ id: "day", label: t("discounts_group_day") },
		{ id: "product", label: t("discounts_group_product") },
	]

	const totals = data?.totals || {}
	const rows = data?.rows || []

	return (
		<>
			<AnalyticsFilterBar
				title={t("analytics_discounts")}
				icon="uil-percentage"
				subtitle={t("discounts_report_subtitle")}
				filters={filters}
				presets={["yesterday", "7days", "30days", "this_month", "last_month", "custom"]}
			>
				<FilterField label={t("branch")}>
					<FilterSelect options={options.poses} allLabel={t("all_branches")} value={draft.pos_id} onChange={(id) => patch({ pos_id: id })} />
				</FilterField>
				<FilterField label={t("order_type")}>
					<FilterSelect isMulti options={options.orderTypes} allLabel={t("all_order_types")} value={draft.order_type_ids} onChange={(ids) => patch({ order_type_ids: ids })} />
				</FilterField>
				<FilterField label={t("source")}>
					<FilterSelect isMulti options={options.sources} allLabel={t("all_sources")} value={draft.source_ids} onChange={(ids) => patch({ source_ids: ids })} />
				</FilterField>
				<FilterField label={t("discount_source")}>
					<FilterSelect isMulti options={sourceOptions} allLabel={t("discount_sources_all")} value={draft.sources} onChange={(ids) => patch({ sources: ids, discount_service_ids: [] })} />
				</FilterField>
				<FilterField label={t("discount_service")} wide>
					<FilterSelect isMulti options={serviceOptions} allLabel={t("discount_services_all")} getOptionLabel={(option) => option.label} value={draft.discount_service_ids} onChange={(ids) => patch({ discount_service_ids: ids })} />
				</FilterField>
				<FilterField label={t("discounts_group_by")} wide>
					<FilterSegment options={groupOptions} value={draft.group_by} onChange={(id) => patch({ group_by: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title={t("discounts_total")}
						value={formatMoney(totals.amount || 0, 0)}
						sublabel={t("discounts_share_hint", { share: formatMoney(totals.discount_share || 0, 1) })}
						icon="uil-percentage"
						color="#2563eb"
					/>
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title={t("cheques_with_discount")}
						value={formatMoney(totals.cheques_count || 0, 0)}
						sublabel={t("of_cheques_count", { count: formatMoney(totals.all_cheques_count || 0, 0) })}
						icon="uil-receipt"
						color="#e0762c"
					/>
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title={t("discount_items")}
						value={formatMoney(totals.items_count || 0, 0)}
						sublabel={t("of_items_count", { count: formatMoney(totals.all_items_count || 0, 0) })}
						icon="uil-utensils"
						color="#7c3aed"
					/>
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title={t("discount_reduced")}
						value={formatMoney(totals.reduced_amount || 0, 0)}
						sublabel={`${formatMoney(totals.lines_count || 0, 0)} · ${t("discount_lines").toLowerCase()}`}
						icon="uil-arrow-down"
						color="#dc2626"
					/>
				</div>
				<div className="col-xl-3 col-md-6">
					<KpiCard
						title={t("discount_gift_nominal")}
						value={formatMoney(totals.nominal_amount || 0, 0)}
						sublabel={t("discount_gift_nominal_hint")}
						icon="uil-gift"
						color="#16a34a"
					/>
				</div>
			</div>

			<DiscountsTable rows={rows} totals={totals} groupBy={data?.group_by || draft.group_by} t={t} />
		</>
	)
}

export default Index
